import { useState, useCallback, useEffect, useRef } from 'react';
import { FormState, FORM_INITIAL, Rol, ROL_ID, UsuarioPayload } from '../types/usuario.types';
import { usuarioService } from '../services/usuarioService';
import { catalogStorage } from '../utils/catalogStorage';
import { getConfig } from '../utils/appConfig';

/** Obtiene el idRol desde el cache de localStorage; si no está, usa el mapa estático. */
const resolverIdRol = (rol: Rol): number =>
  catalogStorage.getIdRol(rol, ROL_ID[rol]);


// ============================================================
// useUsuarioForm – 2 pasos
// Paso 1: Datos personales
// Paso 2: Acceso, rol y estado
// ============================================================

export interface StepError { [field: string]: string }

/** Genera la contraseña por defecto: 3 chars rol (minúscula) + _ + DNI */
const generarPassword = (rol: Rol, dni: string) =>
  `${rol.substring(0, 3).toLowerCase()}_${dni}`;

export const useUsuarioForm = (
  idUsuario: number | null,
  onSuccess: () => void,
  rolInicial?: Rol,
) => {
  const isEditing = idUsuario !== null;

  // Ref para que handleSubmit siempre llame la versión más reciente de onSuccess
  // sin necesidad de incluirla en las dependencias del useCallback.
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  const [form, setForm] = useState<FormState>({
    ...FORM_INITIAL,
    ...(rolInicial && { rol: rolInicial, idRol: resolverIdRol(rolInicial) }),
  });
  const [step, setStep]               = useState(1);
  const [errors, setErrors]           = useState<StepError>({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadingInit, setLoadingInit] = useState(false);

  // ── Auto-generar username y contraseña cuando cambia DNI o rol ─
  useEffect(() => {
    if (form.dni.length === 8) {
      const userPrefix = form.rol.substring(0, 2).toUpperCase();
      const password   = generarPassword(form.rol, form.dni);
      setForm(prev => ({
        ...prev,
        username: `${prev.dni}${userPrefix}`,
        // Para nuevo usuario: siempre auto-genera. Para edición: solo si el campo
        // aún coincide con la estructura por defecto (no fue editado manualmente).
        ...(!isEditing && { password, confirmarPassword: password }),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.dni, form.rol]);

  // ── Cargar usuario en modo edición ────────────────────────
  const loadUsuario = useCallback(async () => {
    if (!isEditing || idUsuario === null) return;
    setLoadingInit(true);
    try {
      const data = await usuarioService.obtener(idUsuario);
      const defaultPass = generarPassword(data.rol, data.dni);
      setForm(prev => ({
        ...prev,
        nombres:         data.nombres,
        apellidos:       data.apellidos,
        dni:             data.dni,
        email:           data.email,
        telefono:        data.telefono        ?? '',
        direccion:       data.direccion       ?? '',
        fechaNacimiento: data.fechaNacimiento ?? '',
        codigoSiagie:    data.codigoSiagie    ?? '',
        username:        data.username,
        rol:             data.rol,
        idRol:           data.idRol,
        activo:          data.activo,
        password:        defaultPass,
        confirmarPassword: defaultPass,
        // Importante: cargar el sexo guardado para que el PUT no lo borre.
        sexo:            data.sexo ?? '',
      }));
    } catch { /* mantiene vacío */ }
    finally { setLoadingInit(false); }
  }, [idUsuario, isEditing]);

  // ── Cambio de campo ───────────────────────────────────────
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    setSubmitError(null);
    const checked = (e.target as HTMLInputElement).checked;
    if (name === 'activo') {
      setForm(prev => ({ ...prev, activo: value === 'true' }));
      return;
    }
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  // ── Cambio de rol (actualiza rol + idRol juntos) ──────────
  const handleRolChange = useCallback((rol: Rol) => {
    setForm(prev => ({ ...prev, rol, idRol: resolverIdRol(rol) }));
    setErrors(prev => { const n = { ...prev }; delete n.rol; return n; });
    setSubmitError(null);
  }, []);

  // ── Validación por paso ───────────────────────────────────
  const validate = useCallback((targetStep: number): StepError => {
    const e: StepError = {};
    if (targetStep === 1) {
      if (!form.nombres.trim())   e.nombres   = 'Requerido';
      if (!form.apellidos.trim()) e.apellidos = 'Requerido';
      if (!/^\d{8}$/.test(form.dni)) e.dni   = 'DNI debe tener 8 dígitos';
      const dominio = getConfig().correodominio || '@gmail.com';
      if (!/\S+@\S+\.\S+/.test(form.email)) {
        e.email = 'Email inválido';
      } else if (dominio !== '@gmail.com' && !form.email.endsWith(dominio)) {
        e.email = `Debe usar el dominio ${dominio}`;
      }
      if (!form.telefono.trim())  e.telefono  = 'Requerido';
      // Sexo es obligatorio para estudiantes (lo necesita el fotocheck / SIAGIE).
      if (form.rol === 'ESTUDIANTE' && form.sexo !== 'M' && form.sexo !== 'F') {
        e.sexo = 'Selecciona el sexo del estudiante';
      }
    }
    if (targetStep === 2 && isEditing) {
      // En edición la contraseña es editable: validar solo si fue modificada
      if (form.password.length < 8) e.password = 'Mínimo 8 caracteres';
      if (form.password !== form.confirmarPassword) e.confirmarPassword = 'Las contraseñas no coinciden';
    }
    return e;
  }, [form, isEditing]);

  const nextStep = useCallback(() => {
    const e = validate(step);
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setStep(s => Math.min(s + 1, 2));
  }, [step, validate]);

  const prevStep = useCallback(() => setStep(s => Math.max(s - 1, 1)), []);

  // ── Construir payload exacto del backend ──────────────────
  const buildPayload = (): UsuarioPayload => ({
    username:        form.username,
    nombres:         form.nombres,
    apellidos:       form.apellidos,
    email:           form.email,
    dni:             form.dni,
    telefono:        form.telefono,
    direccion:       form.direccion.trim()       || null,
    fechaNacimiento: form.fechaNacimiento.trim() || null,
    codigoSiagie:    form.rol === 'ESTUDIANTE' ? (form.codigoSiagie.trim() || null) : null,
    fotoPerfil:      null,
    idRol:           form.idRol,
    activo:          form.activo,
    password:        form.password,   // siempre se envía (nuevo = auto, edición = editable)
    sexo:            form.sexo === '' ? null : form.sexo,
  });

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    const e = validate(2);
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = buildPayload();
      if (isEditing && idUsuario) {
        await usuarioService.modificar(idUsuario, payload);
      } else {
        await usuarioService.crear(payload);
      }
      onSuccessRef.current();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  }, [form, isEditing, idUsuario, validate]);

  const prefillReniec = useCallback((
    nombres: string,
    apellidoPaterno: string,
    apellidoMaterno: string,
    sexo?: 'M' | 'F' | null,
  ) => {
    setForm(prev => ({
      ...prev,
      nombres,
      apellidos: [apellidoPaterno, apellidoMaterno].filter(Boolean).join(' '),
      // Solo prefijar sexo si RENIEC lo trae y el campo aún está vacío
      ...(sexo && prev.sexo === '' ? { sexo } : {}),
    }));
    setErrors(prev => {
      const n = { ...prev };
      delete n.nombres; delete n.apellidos;
      if (sexo) delete n.sexo;
      return n;
    });
  }, []);

  return {
    form, step, errors, submitting, submitError, loadingInit,
    handleChange, handleRolChange, nextStep, prevStep, handleSubmit, loadUsuario,
    prefillReniec,
    totalSteps: 2,
  };
};
